import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Globe, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import { TORONTO_WATERFRONT_IMAGE } from "../lib/brand";

const BUSINESS_TZ = "America/Toronto";

function fmtInTZ(iso, tz, locale) {
  const d = new Date(iso);
  return d.toLocaleString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tz,
    hour12: false,
  });
}

function shortTZ(tz) {
  try {
    return new Intl.DateTimeFormat("en", {
      timeZoneName: "short",
      timeZone: tz,
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}

function groupByDay(slots, tz, locale) {
  if (!Array.isArray(slots)) return [];
  const map = new Map();
  slots.forEach((iso) => {
    if (!iso) return;
    try {
      const d = new Date(iso);
      const key = d.toLocaleDateString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone: tz,
      });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(iso);
    } catch {
      // ignore invalid date
    }
  });
  return Array.from(map.entries());
}

function generateDefaultSlots() {
  const result = [];
  const now = new Date();
  for (let d = 1; d <= 14; d++) {
    const targetDate = new Date(now);
    targetDate.setDate(now.getDate() + d);
    const dayOfWeek = targetDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const day = String(targetDate.getDate()).padStart(2, "0");
    const times = ["14:00:00Z", "15:30:00Z", "17:00:00Z", "18:30:00Z", "20:00:00Z"];
    times.forEach((t) => {
      result.push(`${year}-${month}-${day}T${t}`);
    });
  }
  return result;
}

export default function Booking() {
  const { t, lang } = useLang();
  const locale = lang === "fr" ? "fr-CA" : "en-CA";

  const [slots, setSlots] = useState(() => generateDefaultSlots());
  const [chosen, setChosen] = useState(null);
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const localTZ = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    []
  );

  useEffect(() => {
    api
      .get("/bookings/slots")
      .then((r) => {
        if (Array.isArray(r.data?.slots)) {
          setSlots(r.data.slots);
        }
      })
      .catch(() => {
        // Keep fallback slots
      });
  }, []);

  const groups = useMemo(
    () => groupByDay(slots, localTZ, locale),
    [slots, localTZ, locale]
  );

  const confirm = async () => {
    setErr("");
    if (!chosen || !topic || !fullName || !email) return;
    setBusy(true);
    try {
      await api.post("/bookings", {
        slot_start: chosen,
        topic,
        notes,
        full_name: fullName,
        email,
        phone,
        language: lang,
        kind: "consultation",
      });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-alabaster min-h-[70vh]" data-testid="booking-done">
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 pt-24 pb-16 text-center">
          <p className="font-serif text-6xl text-terracotta mb-4">✓</p>
          <h1 className="font-serif text-5xl text-navy mb-4">
            {lang === "fr" ? "Appel confirmé" : "Call confirmed"}
          </h1>
          <p className="text-navy/70 mb-2">
            {fmtInTZ(chosen, localTZ, locale)} · {shortTZ(localTZ)}
          </p>
          <p className="text-navy/50 text-sm mb-8">
            {fmtInTZ(chosen, BUSINESS_TZ, locale)} · {shortTZ(BUSINESS_TZ)}
          </p>
          <p className="text-navy/70 max-w-md mx-auto mb-8">
            {lang === "fr"
              ? "Un email de confirmation vous a été envoyé. Josias vous fera parvenir le lien de la rencontre 1h avant l'appel."
              : "A confirmation email is on its way. Josias will send you the meeting link 1 hour before the call."}
          </p>
          <Link
            to="/"
            data-testid="booking-done-home"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm hover:bg-terracotta transition"
          >
            {lang === "fr" ? "Retour à l'accueil" : "Back home"}
            <ArrowRight size={14} />
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-alabaster" data-testid="booking-page">
      <PageHero
        eyebrow={lang === "fr" ? "Appel de découverte" : "Discovery call"}
        title={lang === "fr" ? "Réservez 30 min gratuites." : "Book a free 30-min call."}
        subtitle={lang === "fr"
          ? "Choisissez un créneau ci-dessous. Les horaires s’affichent automatiquement dans votre fuseau, où que vous soyez dans le monde."
          : "Choose a slot below. Times are automatically displayed in your timezone, wherever you are in the world."}
        image={TORONTO_WATERFRONT_IMAGE}
        imageAlt={lang === "fr" ? "Toronto et la Tour CN" : "Toronto and the CN Tower"}
        compact
      >
        <div className="flex flex-wrap items-center gap-3 text-xs text-white/75">
          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/15">
            <Globe size={13} className="text-gold" />
            {lang === "fr" ? "Votre fuseau" : "Your timezone"}: <strong className="text-white">{localTZ}</strong>
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/15">
            <Clock size={13} className="text-gold" />
            30 min · {lang === "fr" ? "gratuit" : "free"}
          </span>
        </div>
      </PageHero>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="text-xs tracking-[0.25em] uppercase text-navy/50 mb-4">
            {lang === "fr" ? "Créneaux disponibles" : "Available slots"}
          </p>
          {slots.length === 0 && (
            <p className="text-navy/60 italic">{t("common.loading")}</p>
          )}
          <div className="space-y-8 max-h-[600px] overflow-y-auto pr-2">
            {groups.map(([day, dayslots]) => (
              <div key={day} data-testid={`slot-day-${day}`}>
                <p className="font-serif text-lg text-navy mb-3 capitalize">{day}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {dayslots.map((s) => {
                    const isSel = chosen === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setChosen(s)}
                        data-testid={`slot-${s}`}
                        className={`px-4 py-3 rounded-xl border text-left text-sm transition ${
                          isSel
                            ? "bg-navy text-alabaster border-navy shadow-md"
                            : "bg-white border-navy/10 hover:border-terracotta hover:shadow-sm text-navy"
                        }`}
                      >
                        <span className="block font-serif text-lg leading-tight">
                          {fmtInTZ(s, localTZ, locale).split(", ").pop()}
                        </span>
                        <span
                          className={`block text-[10px] mt-1 ${
                            isSel ? "text-alabaster/70" : "text-navy/50"
                          }`}
                        >
                          {fmtInTZ(s, BUSINESS_TZ, locale).split(", ").pop()} · {shortTZ(BUSINESS_TZ)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <motion.div layout className="bg-white border border-navy/10 rounded-[2rem] p-8 sticky top-24 shadow-[0_20px_60px_rgba(6,22,42,.07)]">
            <p className="text-xs tracking-[0.25em] uppercase text-navy/50 mb-4">
              {lang === "fr" ? "Votre créneau" : "Your slot"}
            </p>
            {chosen ? (
              <div className="mb-6 p-4 rounded-xl bg-cream border border-navy/5">
                <p className="font-serif text-lg text-navy leading-tight">
                  {fmtInTZ(chosen, localTZ, locale)}
                </p>
                <p className="text-xs text-navy/60 mt-1">
                  {shortTZ(localTZ)} · {fmtInTZ(chosen, BUSINESS_TZ, locale)} {shortTZ(BUSINESS_TZ)}
                </p>
                <p className="text-xs text-navy/60 mt-2 inline-flex items-center gap-1">
                  <Clock size={12} />
                  30 min · {lang === "fr" ? "appel gratuit" : "free call"}
                </p>
              </div>
            ) : (
              <p className="text-navy/50 italic text-sm mb-6">
                {lang === "fr" ? "Choisissez un créneau à gauche." : "Pick a slot on the left."}
              </p>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">
                  {lang === "fr" ? "Nom complet" : "Full name"} *
                </label>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  data-testid="booking-name"
                  className="w-full h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="booking-email"
                  className="w-full h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">
                  {lang === "fr" ? "WhatsApp / téléphone" : "WhatsApp / phone"}
                </label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+237..."
                  data-testid="booking-phone"
                  className="w-full h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">
                  {lang === "fr" ? "Sujet de l'appel" : "Call topic"} *
                </label>
                <input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                  placeholder={
                    lang === "fr"
                      ? "Ex. Entrée Express, parrainage, TCF…"
                      : "e.g. Express Entry, sponsorship, TCF…"
                  }
                  data-testid="booking-topic"
                  className="w-full h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">
                  {lang === "fr" ? "Notes (facultatif)" : "Notes (optional)"}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="booking-notes"
                  className="w-full px-3 py-2 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm resize-none"
                />
              </div>
            </div>

            {err && (
              <p
                className="mt-4 text-xs text-terracotta inline-flex items-center gap-2"
                data-testid="booking-error"
              >
                <Info size={12} />
                {err}
              </p>
            )}

            <button
              onClick={confirm}
              disabled={!chosen || !topic || !fullName || !email || busy}
              data-testid="booking-confirm"
              className="mt-6 w-full h-12 rounded-full bg-navy text-alabaster hover:bg-terracotta transition text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy
                ? t("common.loading")
                : lang === "fr"
                ? "Confirmer l'appel gratuit"
                : "Confirm free call"}
              <ArrowRight size={14} />
            </button>
            <p className="mt-3 text-[11px] text-navy/50 text-center">
              {lang === "fr"
                ? "En confirmant, vous acceptez d'être contacté par email pour l'appel."
                : "By confirming, you agree to be contacted by email about the call."}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
